import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Lock, Download, ShieldAlert, FileText, Wallet } from "lucide-react";

const fmtShares = (v) => {
  const n = Number(v) || 0;
  if (!n) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
};
const fmtUSD = (n) => "$" + (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const fmtDate = (v) => {
  if (!v) return "—";
  const head = String(v).split("T")[0];
  const m = head.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : head;
};
const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Public, no-auth read-only renderer for a PFS snapshot shared via token.
 * Lender opens `/share/pfs/{token}` and sees a clean page (no nav, no edit
 * chrome) with the option to download the PDF. Audit views are recorded
 * server-side so the landlord can see who opened what version.
 */
export default function SharedPfs() {
  const { token } = useParams();
  const [phase, setPhase] = useState("loading"); // loading | password | viewing | error
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const m = await axios.get(`${API_BASE}/api/pfs/share/${token}`);
        setMeta(m.data);
        if (m.data.needs_password) {
          setPhase("password");
        } else {
          await loadView("");
        }
      } catch (err) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        if (status === 410) setErrorText(detail || "This link is no longer available.");
        else if (status === 404) setErrorText("This share link doesn't exist.");
        else setErrorText("Could not load this share link.");
        setPhase("error");
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadView = async (pw) => {
    setSubmitting(true);
    setErrorText("");
    try {
      const r = await axios.post(`${API_BASE}/api/pfs/share/${token}/view`, { password: pw });
      setData(r.data);
      setPhase("viewing");
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 401) {
        setErrorText("Password required" + (pw ? " — that password didn't match." : "."));
        setPhase("password");
      } else if (status === 410) {
        setErrorText(detail || "This link is no longer available.");
        setPhase("error");
      } else {
        setErrorText("Could not load this snapshot.");
        setPhase("error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = () => {
    const url = `${API_BASE}/api/pfs/share/${token}/pdf${password ? `?pw=${encodeURIComponent(password)}` : ""}`;
    window.open(url, "_blank");
  };

  if (phase === "loading") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-sm p-8 text-center" data-testid="share-error">
          <ShieldAlert className="w-10 h-10 text-rose-600 mx-auto mb-3" />
          <h1 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit" }}>Link unavailable</h1>
          <p className="text-sm text-slate-600">{errorText}</p>
          <p className="text-xs text-slate-400 mt-4">If you believe this is a mistake, contact the borrower for a new link.</p>
        </div>
      </div>
    );
  }

  if (phase === "password") {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <form
          onSubmit={(e) => { e.preventDefault(); loadView(password); }}
          className="max-w-md w-full bg-white border border-slate-200 rounded-sm p-8"
          data-testid="share-password-form"
        >
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-slate-700" />
            <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Password protected</h1>
          </div>
          {meta?.recipient_label && (
            <div className="text-sm text-slate-600 mb-4">
              Hello <span className="font-semibold text-slate-900">{meta.recipient_label}</span> — this PFS link is password-protected. Enter the password the borrower shared with you.
            </div>
          )}
          <label className="block mb-3">
            <div className="overline text-slate-500 mb-1">Password</div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              className="input-clean w-full"
              data-testid="share-password-input"
            />
          </label>
          {errorText && <div className="text-xs text-rose-600 mb-3">{errorText}</div>}
          <button
            type="submit"
            disabled={submitting || !password}
            className="btn-primary-dark w-full inline-flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="share-password-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
            Unlock PFS
          </button>
        </form>
      </div>
    );
  }

  // phase === "viewing"
  const t = data?.totals || {};
  const snap = data?.snapshot || {};
  const sched = snap.real_estate_schedule || [];
  const sharedBy = data?.shared_by || {};

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            {sharedBy.logo_data_uri ? (
              <img
                src={sharedBy.logo_data_uri}
                alt={sharedBy.company || "Company logo"}
                className="w-10 h-10 object-contain bg-white rounded-sm p-0.5"
                data-testid="share-pfs-logo"
              />
            ) : (
              <Wallet className="w-6 h-6 text-emerald-400" />
            )}
            <div>
              <div className="text-[10px] uppercase tracking-widest font-mono text-slate-400">
                {sharedBy.company ? `${sharedBy.company} · ` : ""}Personal Financial Statement
              </div>
              <div className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>
                {snap.name || "Borrower"}
                {snap.business_name && <span className="text-slate-300 font-normal"> {snap.business_name}</span>}
              </div>
            </div>
          </div>
          <button
            onClick={downloadPdf}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-sm inline-flex items-center gap-2"
            data-testid="share-download-pdf"
          >
            <Download className="w-4 h-4" /> Download PDF
          </button>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6" data-testid="share-pfs-page">
        {data?.recipient_label && (
          <div className="text-sm text-slate-600">
            Shared with <span className="font-semibold text-slate-900">{data.recipient_label}</span>
            {snap.as_of_date && <> &middot; <span className="font-mono">As of {fmtDate(snap.as_of_date)}</span></>}
          </div>
        )}

        {/* KPI band */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Kpi label="Total assets" value={t.total_assets} />
          <Kpi label="Total liabilities" value={t.total_liabilities} />
          <Kpi label="Net worth" value={t.net_worth} tone={(t.net_worth || 0) >= 0 ? "emerald" : "rose"} />
        </div>

        {/* Assets / Liabilities */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Block title="Assets">
            <Line
              label={(snap.cash_schedule && snap.cash_schedule.length) ? "Cash on hand & in banks (see schedule)" : "Cash on hand & in banks"}
              value={(snap.cash_schedule && snap.cash_schedule.length) ? t.cash_schedule_total : snap.cash_on_hand}
              muted={!!(snap.cash_schedule && snap.cash_schedule.length)}
            />
            <Line label="Savings accounts" value={snap.savings} />
            <Line label="IRA / Retirement accounts" value={snap.retirement_accounts} />
            <Line label="Accounts & notes receivable" value={snap.accounts_receivable} />
            <Line label="Life insurance — cash surrender value" value={snap.life_insurance_cash_value} />
            <Line
              label={(snap.stocks_bonds_schedule && snap.stocks_bonds_schedule.length) ? "Stocks & bonds (see schedule)" : "Stocks & bonds"}
              value={(snap.stocks_bonds_schedule && snap.stocks_bonds_schedule.length) ? t.stocks_bonds_value : snap.stocks_bonds}
              muted={!!(snap.stocks_bonds_schedule && snap.stocks_bonds_schedule.length)}
            />
            <Line label="Real estate (see schedule)" value={t.real_estate_value} muted />
            <Line
              label={(snap.automobiles && snap.automobiles.length) ? "Automobile (see schedule)" : "Automobile — present value"}
              value={(snap.automobiles && snap.automobiles.length) ? t.automobiles_value : snap.automobile_value}
              muted={!!(snap.automobiles && snap.automobiles.length)}
            />
            <Line
              label={(snap.other_personal_property_schedule && snap.other_personal_property_schedule.length) ? "Other personal property (see schedule)" : "Other personal property"}
              value={(snap.other_personal_property_schedule && snap.other_personal_property_schedule.length) ? t.other_personal_property_value : snap.other_personal_property}
              muted={!!(snap.other_personal_property_schedule && snap.other_personal_property_schedule.length)}
            />
            <Line
              label={(snap.other_assets_schedule && snap.other_assets_schedule.length) ? "Other assets (see schedule)" : "Other assets"}
              value={(snap.other_assets_schedule && snap.other_assets_schedule.length) ? t.other_assets_value : snap.other_assets}
              muted={!!(snap.other_assets_schedule && snap.other_assets_schedule.length)}
            />
            <Line label="Total assets" value={t.total_assets} bold />
          </Block>
          <Block title="Liabilities">
            <Line label="Accounts payable" value={snap.accounts_payable} />
            <Line
              label={(snap.notes_payable_schedule && snap.notes_payable_schedule.length) ? "Notes payable to banks (see schedule)" : "Notes payable to banks"}
              value={(snap.notes_payable_schedule && snap.notes_payable_schedule.length) ? t.notes_payable_balance : snap.notes_payable}
              muted={!!(snap.notes_payable_schedule && snap.notes_payable_schedule.length)}
            />
            <Line
              label={(snap.auto_loans_schedule && snap.auto_loans_schedule.length) ? "Auto installment loans (see schedule)" : "Auto installment loans"}
              value={(snap.auto_loans_schedule && snap.auto_loans_schedule.length) ? t.auto_loans_balance : snap.auto_loans}
              muted={!!(snap.auto_loans_schedule && snap.auto_loans_schedule.length)}
            />
            <Line
              label={(snap.other_installment_loans_schedule && snap.other_installment_loans_schedule.length) ? "Other installment loans (see schedule)" : "Other installment loans"}
              value={(snap.other_installment_loans_schedule && snap.other_installment_loans_schedule.length) ? t.other_installment_loans_balance : snap.other_installment_loans}
              muted={!!(snap.other_installment_loans_schedule && snap.other_installment_loans_schedule.length)}
            />
            <Line label="Loan on life insurance" value={snap.loan_on_life_insurance} />
            <Line label="Mortgages on real estate (see schedule)" value={t.real_estate_mortgage_balance} muted />
            <Line label="Unpaid taxes" value={snap.unpaid_taxes} />
            <Line label="Other liabilities" value={snap.other_liabilities} />
            <Line label="Total liabilities" value={t.total_liabilities} bold />
          </Block>
        </div>

        {/* Income + Contingent */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Block title="Annual income">
            <Line label="Salary / wages" value={snap.salary} />
            <Line label="Net investment income" value={snap.net_investment_income} />
            <Line label="Real estate income (rents)" value={snap.real_estate_income} />
            <Line label="Other income" value={snap.other_income} />
            <Line label="Total annual income" value={t.total_annual_income} bold />
          </Block>
          <Block title="Contingent liabilities">
            <Line label="As endorser or co-maker" value={snap.contingent_endorser} />
            <Line label="Legal claims & judgments" value={snap.contingent_legal} />
            <Line label="Provision for federal income tax" value={snap.contingent_taxes} />
            <Line label="Other contingent liabilities" value={snap.contingent_other} />
            <Line label="Total contingent" value={t.total_contingent_liabilities} bold />
          </Block>
        </div>

        {/* Stocks & Bonds Schedule */}
        {snap.stocks_bonds_schedule && snap.stocks_bonds_schedule.length > 0 && (
          <Block title="Schedule of stocks & bonds">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest font-mono text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-2 w-12">#</th>
                    <th className="py-2 px-2 w-24">Symbol</th>
                    <th className="py-2 px-2 text-right w-24">Shares</th>
                    <th className="py-2 px-2 text-right w-32">Cost basis</th>
                    <th className="py-2 px-2 text-right">Present value</th>
                  </tr>
                </thead>
                <tbody>
                  {snap.stocks_bonds_schedule.map((s, idx) => (
                    <tr key={idx} className="border-b border-slate-100">
                      <td className="py-2 pr-2 text-slate-500">{idx + 1}</td>
                      <td className="py-2 px-2 font-mono uppercase">{s.symbol || "—"}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{fmtShares(s.shares)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{fmtUSD(s.cost_basis)}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{fmtUSD(s.present_value)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 font-semibold">
                    <td colSpan={4} className="py-2 pr-2 text-right text-slate-700">Total</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtUSD(t.stocks_bonds_value)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Block>
        )}

        {/* Other Personal Property Schedule */}
        {snap.other_personal_property_schedule && snap.other_personal_property_schedule.length > 0 && (
          <DescribedScheduleBlock
            title="Schedule of other personal property"
            rows={snap.other_personal_property_schedule}
            total={t.other_personal_property_value}
          />
        )}

        {/* Other Assets Schedule */}
        {snap.other_assets_schedule && snap.other_assets_schedule.length > 0 && (
          <DescribedScheduleBlock
            title="Schedule of other assets"
            rows={snap.other_assets_schedule}
            total={t.other_assets_value}
          />
        )}

        {/* Notes Payable Schedule */}
        {snap.notes_payable_schedule && snap.notes_payable_schedule.length > 0 && (
          <LiabilityScheduleBlock
            title="Schedule of notes payable to banks"
            rows={snap.notes_payable_schedule}
            totalBalance={t.notes_payable_balance}
            showDescription={false}
          />
        )}

        {/* Auto Loans Schedule */}
        {snap.auto_loans_schedule && snap.auto_loans_schedule.length > 0 && (
          <LiabilityScheduleBlock
            title="Schedule of auto installment loans"
            rows={snap.auto_loans_schedule}
            totalBalance={t.auto_loans_balance}
            showDescription={true}
            descriptionLabel="Vehicle"
          />
        )}

        {/* Other Installment Loans Schedule */}
        {snap.other_installment_loans_schedule && snap.other_installment_loans_schedule.length > 0 && (
          <LiabilityScheduleBlock
            title="Schedule of other installment loans"
            rows={snap.other_installment_loans_schedule}
            totalBalance={t.other_installment_loans_balance}
            showDescription={true}
            descriptionLabel="Description"
          />
        )}

        {/* Cash on hand & in banks Schedule */}
        {snap.cash_schedule && snap.cash_schedule.length > 0 && (
          <Block title="Schedule of cash on hand & in banks">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest font-mono text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-2 w-12">#</th>
                    <th className="py-2 px-2">Financial institution</th>
                    <th className="py-2 px-2">Location</th>
                    <th className="py-2 px-2 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {snap.cash_schedule.map((c, idx) => (
                    <tr key={`cash-${idx}-${c.institution || ""}`} className="border-b border-slate-100">
                      <td className="py-2 pr-2 text-slate-500">{idx + 1}</td>
                      <td className="py-2 px-2">{c.institution || "—"}</td>
                      <td className="py-2 px-2">{c.location || "—"}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{fmtUSD(c.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 font-semibold">
                    <td colSpan={3} className="py-2 pr-2 text-right text-slate-700">Total</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtUSD(t.cash_schedule_total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Block>
        )}

        {/* Automobiles Schedule */}
        {snap.automobiles && snap.automobiles.length > 0 && (
          <Block title="Schedule of automobiles">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest font-mono text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-2 w-12">#</th>
                    <th className="py-2 px-2 w-20">Year</th>
                    <th className="py-2 px-2">Make</th>
                    <th className="py-2 px-2">Model</th>
                    <th className="py-2 px-2 text-right">Present value</th>
                  </tr>
                </thead>
                <tbody>
                  {snap.automobiles.map((a, idx) => (
                    <tr key={idx} className="border-b border-slate-100">
                      <td className="py-2 pr-2 text-slate-500">{idx + 1}</td>
                      <td className="py-2 px-2 tabular-nums">{a.year || "—"}</td>
                      <td className="py-2 px-2">{a.make || "—"}</td>
                      <td className="py-2 px-2">{a.model || "—"}</td>
                      <td className="py-2 px-2 text-right tabular-nums">{fmtUSD(a.present_value)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 font-semibold">
                    <td colSpan={4} className="py-2 pr-2 text-right text-slate-700">Total</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtUSD(t.automobiles_value)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Block>
        )}

        {/* Real Estate Schedule */}
        <Block title="Schedule of real estate owned">
          {sched.length === 0 ? (
            <div className="text-sm text-slate-500">No real estate on file.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest font-mono text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-2">Address / Type</th>
                    <th className="py-2 px-2">Title in</th>
                    <th className="py-2 px-2 text-right">% Own</th>
                    <th className="py-2 px-2">Acquired</th>
                    <th className="py-2 px-2 text-right">Cost</th>
                    <th className="py-2 px-2 text-right">Present value</th>
                    <th className="py-2 px-2">Mortgage holder</th>
                    <th className="py-2 px-2 text-right">Balance</th>
                    <th className="py-2 px-2 text-right">Pmt/mo</th>
                    <th className="py-2 px-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sched.map((r, idx) => (
                    <tr key={idx} className="border-b border-slate-100">
                      <td className="py-2 pr-2 align-top">
                        <div className="text-slate-900">{r.address || "—"}</div>
                        {r.property_type && <div className="text-xs text-slate-500">{r.property_type}</div>}
                      </td>
                      <td className="py-2 px-2 align-top">{r.title_in_name_of || "—"}</td>
                      <td className="py-2 px-2 align-top text-right tabular-nums">{Math.round(Number(r.ownership_pct) || 0)}%</td>
                      <td className="py-2 px-2 align-top">{fmtDate(r.purchase_date)}</td>
                      <td className="py-2 px-2 align-top text-right tabular-nums">{fmtUSD(r.original_cost)}</td>
                      <td className="py-2 px-2 align-top text-right tabular-nums">{fmtUSD(r.present_value)}</td>
                      <td className="py-2 px-2 align-top">{r.mortgage_holder || "—"}</td>
                      <td className="py-2 px-2 align-top text-right tabular-nums">{fmtUSD(r.mortgage_balance)}</td>
                      <td className="py-2 px-2 align-top text-right tabular-nums">{fmtUSD(r.monthly_payment)}</td>
                      <td className="py-2 px-2 align-top">{r.status || "—"}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-300 font-semibold">
                    <td colSpan={5} className="py-2 pr-2 text-right text-slate-700">Totals</td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtUSD(t.real_estate_value)}</td>
                    <td></td>
                    <td className="py-2 px-2 text-right tabular-nums">{fmtUSD(t.real_estate_mortgage_balance)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </Block>

        {snap.notes && (
          <Block title="Notes">
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{snap.notes}</p>
          </Block>
        )}

        <div className="text-center text-xs text-slate-400 italic mt-8">
          This document was shared with you as a read-only snapshot. Borrower certifies the information is accurate as of {fmtDate(snap.as_of_date) || "the date stated"}.
        </div>
      </main>
    </div>
  );
}

function Kpi({ label, value, tone }) {
  const toneClass = tone === "emerald" ? "text-emerald-700" : tone === "rose" ? "text-rose-700" : "text-slate-900";
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-4">
      <div className="overline text-slate-500">{label}</div>
      <div className={`text-3xl font-bold font-mono tabular-nums tracking-tight ${toneClass}`}>
        {fmtUSD(value)}
      </div>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-sm p-5">
      <div className="overline text-slate-700 mb-3 flex items-center gap-2">
        <FileText className="w-3.5 h-3.5" /> {title}
      </div>
      <div className="space-y-1.5">{children}</div>
    </section>
  );
}

function Line({ label, value, muted, bold }) {
  return (
    <div className={`grid grid-cols-[1fr_auto] gap-2 py-1 ${bold ? "border-t border-slate-300 pt-2 mt-1 font-bold text-slate-900" : ""}`}>
      <div className={`text-sm ${muted ? "italic text-slate-500" : "text-slate-700"}`}>{label}</div>
      <div className="text-sm font-mono tabular-nums text-right">{fmtUSD(value)}</div>
    </div>
  );
}

function LiabilityScheduleBlock({ title, rows, totalBalance, showDescription, descriptionLabel }) {
  const totalOriginal = rows.reduce((a, r) => a + (Number(r.original_amount) || 0), 0);
  const totalPayment = rows.reduce((a, r) => a + (Number(r.monthly_payment) || 0), 0);
  return (
    <Block title={title}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest font-mono text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-2 w-12">#</th>
              <th className="py-2 px-2">Lender</th>
              {showDescription && <th className="py-2 px-2">{descriptionLabel || "Description"}</th>}
              <th className="py-2 px-2 text-right w-28">Original</th>
              <th className="py-2 px-2 text-right w-28">Current balance</th>
              <th className="py-2 px-2 text-right w-24">Pmt/mo</th>
              <th className="py-2 px-2 w-28">Maturity</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx} className="border-b border-slate-100">
                <td className="py-2 pr-2 text-slate-500">{idx + 1}</td>
                <td className="py-2 px-2 text-slate-800">{r.lender || "—"}</td>
                {showDescription && (
                  <td className="py-2 px-2 text-slate-700">{r.description || "—"}</td>
                )}
                <td className="py-2 px-2 text-right tabular-nums">{fmtUSD(r.original_amount)}</td>
                <td className="py-2 px-2 text-right tabular-nums">{fmtUSD(r.current_balance)}</td>
                <td className="py-2 px-2 text-right tabular-nums">{fmtUSD(r.monthly_payment)}</td>
                <td className="py-2 px-2 tabular-nums text-slate-700">{fmtDate(r.maturity_date) || "—"}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-300 font-semibold">
              <td colSpan={showDescription ? 3 : 2} className="py-2 pr-2 text-right text-slate-700">Totals</td>
              <td className="py-2 px-2 text-right tabular-nums">{fmtUSD(totalOriginal)}</td>
              <td className="py-2 px-2 text-right tabular-nums">{fmtUSD(totalBalance)}</td>
              <td className="py-2 px-2 text-right tabular-nums">{fmtUSD(totalPayment)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Block>
  );
}

function DescribedScheduleBlock({ title, rows, total }) {
  return (
    <Block title={title}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest font-mono text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-2 w-12">#</th>
              <th className="py-2 px-2">Description</th>
              <th className="py-2 px-2 text-right w-32">Present value</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx} className="border-b border-slate-100">
                <td className="py-2 pr-2 text-slate-500">{idx + 1}</td>
                <td className="py-2 px-2 text-slate-800">{r.description || "—"}</td>
                <td className="py-2 px-2 text-right tabular-nums">{fmtUSD(r.present_value)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-slate-300 font-semibold">
              <td colSpan={2} className="py-2 pr-2 text-right text-slate-700">Total</td>
              <td className="py-2 px-2 text-right tabular-nums">{fmtUSD(total)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </Block>
  );
}
